import * as SHA256 from 'crypto-js/sha256';
import type { I18nService } from '../i18n/i18n.service';
import type { ConfigService } from '../App/services/mx-config-service';
import type { EventService } from '../App/services/event-service';
import { EventCodes } from '../App/model/event-codes';
import type { MxApplicationService } from '../App/services/mx-application-service';
import type { AuthBehaviorProviderService } from '../Auth/mx-auth-behavior-provider-service';
import { Telemetry } from './Telemetry';
import type { TrackOptions } from './telemetry.contracts';
import type { DataStorage } from '../App/services/mx-data-storage';
import { SystemStorageType } from '../App/model/system-storage-types';
import type { IDataProvider } from '../App/services/mx-data-storage';
import type { UserProfileService } from '../App/services/user-profile-service';
import type { MxNavigationService } from '../Navigation/mx-navigation-service';

const signInStorageKey = 'mx.telemetry.signIn';
const trackedApplications = [
	'SelfServicePortal',
	'ServiceDesk',
	'Assets',
	'ServiceCatalog',
	'MasterData',
	'SolutionBuilder',
	'EQM',
	'Contracts',
	'Licenses',
	'LayoutDesigner',
	'UEM',
	'BI',
	'SUEM',
	'OracleCompliance',
];

export class TelemetryService {
	static readonly $inject = [
		'mx.shell.Config',
		'mx.shell.InternationalizationService',
		'mx.shell.EventService',
		'mx.shell.AppsService',
		'mx.shell.AuthBehaviorProviderService',
		'mx.shell.DataStorage',
		'mx.shell.UserProfileService',
		'mx.shell.NavigationService',
		'$rootScope',
	];

	private telemetry: Telemetry;

	private localStorage: IDataProvider;

	private isIdentified = false;

	private preIdentityEvents: Omit<TrackOptions, 'applicationTitle' | 'applicationId'>[] = [];

	constructor(
		private readonly config: ConfigService,
		private readonly i18nService: I18nService,
		private readonly eventService: EventService,
		private readonly applicationService: MxApplicationService,
		private readonly authBehaviour: AuthBehaviorProviderService,
		private readonly dataStorage: DataStorage,
		private readonly userProfileService: UserProfileService,
		private readonly navigationService: MxNavigationService,
		private readonly $rootScope: angular.IRootScopeService
	) {
		this.telemetry = new Telemetry({
			token: this.config.get('telemetryToken'),
			productVersion: this.config.get('productVersion'),
			envId: this.config.get('envId'),
			envType: this.config.get('envType'),
			licenseId: this.config.get('licenseId'),
			customerName: this.config.get('customerName'),
			customerId: this.config.get('customerId'),
			debugTelemetry: this.config.get('debugTelemetry') || false,
			language: this.i18nService.currentLanguage?.code,
		});

		this.localStorage = this.dataStorage.getStorage(SystemStorageType.LocalStorage);

		if (!this.telemetry.enabled) {
			return;
		}

		this.authBehaviour.onSessionEstablished(async ({ isNew }: { isNew: boolean }) => {
			if (isNew) {
				this.localStorage.setAsObject(signInStorageKey, isNew);
			}
		});

		this.authBehaviour.onBeforeSessionDestroy(() => {
			this.track({
				eventName: 'User Signed Out',
			});

			this.telemetry.reset();
		});

		this.eventService.subscribeOnce(EventCodes.ShellAppInitialized, async () => {
			const isNew = this.localStorage.getAsObject(signInStorageKey);
			this.localStorage.remove(signInStorageKey);

			await this.identify();

			if (isNew) {
				this.trackAuth({
					eventName: 'User Signed In',
				});
			}
		});
	}

	public async init() {
		if (this.telemetry.enabled) {
			this.eventService.subscribeGlobal(EventCodes.TrackTelemetry, (event, trackData: TrackOptions) => {
				this.trackAuth(trackData);
			});
		}
	}

	private async identify() {
		const profile = await this.userProfileService.get();

		this.telemetry.updateConfig({
			language: this.i18nService.currentLanguage?.code,
			roles: profile.invariantRoleNames,
		});

		this.telemetry.identify(
			SHA256(`${profile.objectId || profile.id}aBra8fpqwWn5ka${this.config.get('envId')}`.toLowerCase())
				.toString()
				.toLowerCase()
		);
		this.isIdentified = true;
		this.preIdentityEvents.forEach((event) => {
			this.track(event);
		});
		this.preIdentityEvents = [];
	}

	private track(trackOptions: Omit<TrackOptions, 'applicationTitle' | 'applicationId'>) {
		try {
			const { value: applicationId, name: applicationTitle } = this.applicationService.getCurrentApplication() || {};
			const { id: navigationId, technicalName: navigationTitle } = this.navigationService.currentItem || {};

			if (!trackedApplications.includes(applicationTitle)) {
				return;
			}

			this.telemetry.track({
				...trackOptions,
				applicationId,
				applicationTitle,
				navigationId,
				navigationTitle,
				isContrastTheme: this.$rootScope?.themeConfig?.isContrastTheme,
			} as TrackOptions);
		} catch (e) {
			if (this.config.get('debugTelemetry')) {
				console.error(e);
			}
		}
	}

	private trackAuth(trackOptions: Omit<TrackOptions, 'applicationTitle' | 'applicationId'>) {
		if (this.isIdentified) {
			this.track(trackOptions);
		} else {
			this.preIdentityEvents.push(trackOptions);
		}
	}
}
